/**
 * Crew dialogue generator — turns the raw stage-output JSON into a plain-English
 * back-and-forth between the six production-crew agents, in the style of an
 * audience-facing "show" panel. Zero LLM calls; pure deterministic templating
 * from the trace shape defined in backend/crew/orchestrator.py.
 *
 * Each stage contributes 1-2 speech bubbles. Output shape:
 *   [{ agent, role, color, line, meta? }, ...]
 */

const VOICES = {
  producer: {
    name: 'Producer',
    role: 'Moderator',
    color: '#f59e0b',
    cue: 'Producer, moderating.',
  },
  nova: {
    name: 'Nova',
    role: 'Weather + environment',
    color: '#7dd3fc',
    cue: 'Nova, watching the room.',
  },
  orion: {
    name: 'Orion',
    role: 'Science desk',
    color: '#a78bfa',
    cue: 'Orion, science desk.',
  },
  atlas: {
    name: 'Atlas',
    role: 'Geopolitics + markets',
    color: '#f97316',
    cue: 'Atlas, strategy desk.',
  },
  vega: {
    name: 'Vega',
    role: 'Infrastructure ops',
    color: '#34d399',
    cue: 'Vega, operations.',
  },
  wire: {
    name: 'Wire',
    role: 'News desk',
    color: '#22d3ee',
    cue: 'Wire, news desk.',
  },
  scribe: {
    name: 'Scribe',
    role: 'Fact desk',
    color: '#a78bfa',
    cue: 'Scribe, fact desk.',
  },
  mercury: {
    name: 'Mercury',
    role: 'Showrunner',
    color: '#60a5fa',
    cue: 'Mercury calling it.',
  },
  quill: {
    name: 'Quill',
    role: 'Writer',
    color: '#d4a574',
    cue: 'Quill here.',
  },
  frame: {
    name: 'Frame',
    role: 'Director',
    color: '#f472b6',
    cue: 'Frame, taking the scene.',
  },
  stenographer: {
    name: 'Stenographer',
    role: 'Archive',
    color: '#94a3b8',
    cue: 'Stenographer — logging for air.',
  },
};

function _fmt(n) {
  if (n == null) return '—';
  return Number(n).toFixed(2);
}

function linesForStage(stage) {
  const voice = VOICES[stage.agent] || { name: stage.agent, color: '#dbe3f2' };
  const out = stage.output || {};
  const input = stage.input || {};
  const agent = stage.agent;

  if (agent === 'wire') {
    const dup = out.is_duplicate;
    const topic = (out.topic_id || '').slice(0, 8);
    const headline = input.headline || input.topic_candidate?.headline || '';
    const headlineClip = headline ? `"${String(headline).slice(0, 120)}${headline.length > 120 ? '…' : ''}"` : 'the incoming topic';
    if (dup) {
      return [{ ...voice, line: `Already on the board. Skipping ${headlineClip} — same topic ran earlier.` }];
    }
    return [{
      ...voice,
      line: `Fresh topic on the wire: ${headlineClip}. Filed as ${topic}. Not a duplicate — moving it forward.`,
    }];
  }

  if (agent === 'scribe') {
    const verdict = out.verdict || 'unknown';
    const confidence = _fmt(out.confidence);
    const note = out.note || '';
    const verdictLine = {
      verified: `Two independent sources line up. Calling it verified — confidence ${confidence}.`,
      partial: `Only partial corroboration so far. Confidence ${confidence}. ${note ? `(${note})` : ''}`,
      disputed: `I'm seeing conflicting sourcing. Flagging disputed — confidence ${confidence}. Run it as a challenge segment.`,
      unverified: `Single source, no cross-check yet. Holding at unverified, confidence ${confidence}.`,
    }[verdict] || `Verdict: ${verdict}. Confidence ${confidence}. ${note}`;
    return [{ ...voice, line: verdictLine }];
  }

  if (agent === 'mercury') {
    const decision = out.decision || 'approved';
    const reason = out.reason || '';
    const phrases = {
      approved: `Approved. Put it in the A-block.`,
      redirected: `Redirecting — we'll retool this as a challenge piece.`,
      killed: `Killing it. Not strong enough to air.`,
      rejected: `Rejected. Pulling it from the rundown.`,
      held: `Holding for now — need more sourcing before we commit.`,
    };
    return [{
      ...voice,
      line: `${phrases[decision] || `Decision: ${decision}.`}${reason ? ` Rationale: ${reason}.` : ''}`,
    }];
  }

  if (agent === 'quill') {
    const state = out.draft_state || 'stub';
    const words = out.word_count || 0;
    const segment = out.segment || '—';
    const preview = (out.preview || out.text || '').slice(0, 160);
    if (state === 'llm' && words > 0) {
      return [
        {
          ...voice,
          line: `Drafting a ${segment.replace(/_/g, ' ')} — ${words} words. Leading with: "${preview}${preview.length >= 160 ? '…' : ''}"`,
        },
      ];
    }
    if (state === 'stub') {
      return [{
        ...voice,
        line: `Draft desk is offline or key is missing — falling back to a stub for ${segment.replace(/_/g, ' ')}.`,
      }];
    }
    return [{ ...voice, line: `Draft state: ${state} · ${words} words on ${segment}.` }];
  }

  if (agent === 'frame') {
    const scene = out.scene_state || out.scene || 'standard';
    const severity = out.severity_band || out.severity || input.severity || 'nominal';
    const lowerThird = out.lower_third || out.lowerthird || '';
    return [{
      ...voice,
      line: `Scene: ${String(scene).replace(/_/g, ' ')} · severity band ${severity}.${lowerThird ? ` Lower-third: "${lowerThird}".` : ''} Camera is ready.`,
    }];
  }

  if (agent === 'stenographer') {
    const transcript = out.transcript_id || out.archive_id || '—';
    const claims = out.claim_count ?? out.claims?.length ?? 0;
    return [{
      ...voice,
      line: `Logged for air. Transcript ${String(transcript).slice(0, 10)}, ${claims} claim${claims === 1 ? '' : 's'} tagged for fact-check archive.`,
    }];
  }

  // Unknown agent — fall back to a short JSON preview in natural tone.
  const keys = Object.keys(out || {});
  return [{
    ...voice,
    line: keys.length
      ? `${keys.slice(0, 3).map((k) => `${k}: ${JSON.stringify(out[k])}`).join(' · ')}`
      : `Stage complete.`,
  }];
}

/**
 * Given a Crew run payload, produce an ordered list of dialogue bubbles
 * suitable for the audience-facing SHOW panel.
 */
export function dialogueFromRun(run) {
  if (!run || !Array.isArray(run.trace)) return [];
  const bubbles = [];
  (run.trace || []).forEach((stage) => {
    for (const line of linesForStage(stage)) {
      bubbles.push(line);
    }
  });
  return bubbles;
}

export const CREW_VOICES = VOICES;
